/**
 * Atlas Casino - Main Application
 * Uses React.lazy for code splitting and Suspense for loading states
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { ChainProvider } from './contexts/ChainContext.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import AdminRoute from './components/AdminRoute.jsx';
import { theme } from './constants/theme.js';
import { GameLobbySkeleton, GameLoadingSkeleton, DashboardSkeleton } from './components/SkeletonLoaders.jsx';
import { captureRefFromUrl } from './utils/referral.js';
import { captureAttribution } from './utils/attribution.js';

// Record first-touch acquisition data (utm_* tags, referrer, landing page) for
// the admin Traffic tab. Must run before captureRefFromUrl(), which strips
// `?ref` from the address bar. localStorage only — Amplify isn't configured yet.
captureAttribution();

// Capture any `?ref=CODE` before React mounts, so a referral survives the user
// browsing the public site prior to signing up. (Runs once on module load.)
captureRefFromUrl();

// Lazy load pages for code splitting - each page loads only when needed
const Homepage = lazy(() => import('./pages/Homepage.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const GameLobby = lazy(() => import('./pages/GameLobby.jsx'));
const DepositPage = lazy(() => import('./pages/DepositPage.jsx'));
const GameHistoryPage = lazy(() => import('./pages/GameHistoryPage.jsx'));
const WithdrawPage = lazy(() => import('./pages/WithdrawPage.jsx'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage.jsx'));
const AdminWithdrawalsPage = lazy(() => import('./pages/AdminWithdrawalsPage.jsx'));
const AdminReferralsPage = lazy(() => import('./pages/AdminReferralsPage.jsx'));
const SettingsPage = lazy(() => import('./pages/SettingsPage.jsx'));

// Atlas Original Games — every game (live + demo) flows through GamePage
const GamePage = lazy(() => import('./games/GamePage.jsx'));

// BGaming Demo Player
const BGamingPlayer = lazy(() => import('./pages/BGamingPlayer.jsx'));

/**
 * Loading Fallback Component
 * Premium loading state with animated casino theme
 */
function LoadingFallback() {
  return (
    <div
      className="h-dvh flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: theme.bg }}
    >
      {/* Animated background gradient */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(123,47,247,0.2) 0%, transparent 50%)',
          animation: 'pulse 2s ease-in-out infinite',
        }}
      />

      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-10px) scale(1.05); }
        }
        @keyframes chipSpin {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div className="text-center relative z-10">
        {/* Animated logo */}
        <div
          className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center text-white text-3xl font-bold"
          style={{
            background: theme.grad,
            boxShadow: `${theme.purpleGlow}, inset 0 2px 10px rgba(255,255,255,0.2)`,
            animation: 'logoFloat 2s ease-in-out infinite',
          }}
        >
          A
        </div>

        {/* Loading dots */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor: theme.purple,
                animation: `dotPulse 1s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>

        <p className="text-base font-medium" style={{ color: theme.t3 }}>
          Loading Atlas Casino
        </p>
        <p className="text-xs mt-1" style={{ color: theme.t5 }}>
          Preparing your gaming experience...
        </p>
      </div>
    </div>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <ErrorBoundary showReload>
      {/*
        Authenticator.Provider hoists Amplify's auth state above every
        ProtectedRoute so route changes / StrictMode double-mounts don't
        re-instantiate the Authenticator's internal state machine. Without
        this, React 19 StrictMode wedges the Authenticator render-prop on
        localhost dev and renders a blank page after sign-in (2026-06-22).
      */}
      <Authenticator.Provider>
      <AuthProvider>
        <ChainProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1A1F2E',
              color: '#fff',
              border: `1px solid ${theme.border}`,
              fontFamily: theme.fontPrimary,
            },
            success: { iconTheme: { primary: theme.teal, secondary: '#fff' } },
            error: { iconTheme: { primary: theme.error, secondary: '#fff' } },
          }}
        />
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Homepage />} />
              <Route path="/games" element={
                <Suspense fallback={<GameLobbySkeleton />}>
                  <GameLobby />
                </Suspense>
              } />
              <Route path="/leaderboard" element={<LeaderboardPage />} />

              {/* Protected routes - require authentication */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Suspense fallback={<DashboardSkeleton />}>
                    <Dashboard />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* BGaming Demo Games (public - iframe) — must be before /demo/:gameId */}
              <Route path="/demo/bgaming/:gameId" element={<BGamingPlayer />} />
              {/* Atlas Legends Games - Demo mode (public) */}
              <Route path="/demo/:gameId" element={
                <Suspense fallback={<GameLoadingSkeleton />}>
                  <GamePage />
                </Suspense>
              } />
              {/* Atlas Legends Games - Real ETH (requires auth) */}
              <Route path="/game/:gameId" element={
                <ProtectedRoute>
                  <Suspense fallback={<GameLoadingSkeleton />}>
                    <GamePage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/deposit" element={
                <ProtectedRoute>
                  <DepositPage />
                </ProtectedRoute>
              } />
              {/* Additional dashboard routes */}
              <Route path="/withdraw" element={
                <ProtectedRoute>
                  <WithdrawPage />
                </ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute>
                  <GameHistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/withdrawals" element={
                <AdminRoute>
                  <AdminWithdrawalsPage />
                </AdminRoute>
              } />
              <Route path="/admin/referrals" element={
                <AdminRoute>
                  <AdminReferralsPage />
                </AdminRoute>
              } />

              {/* Fallback — redirect unknown URLs to the homepage so the
                  bar doesn't keep the garbage path visible. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </ChainProvider>
      </AuthProvider>
      </Authenticator.Provider>
    </ErrorBoundary>
  );
}

export default App;
